import React, { useMemo, useRef } from "react";
import { SaveViewButton } from "./SaveViewButton";
import { SavedViewChips } from "./SavedViewChips";
import { useSavedHeaderViews } from "./useSavedHeaderViews";
import { flattenHeaders, isGroupedHeaderList } from "./headerGroupUtils";

// Single entry point for adding "Save View" to any table: bundles
// useSavedHeaderViews + SavedViewChips + SaveViewButton into the exact three
// values a page needs to hand to CustomFilter/its table, so wiring a new
// report up only takes a handful of lines. See ESIReport for the reference
// usage (single scope) and SalaryStatementReport for a multi-scope one (a
// report-type switcher).
//
//   const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
//     domain, userId: userDetails?.id, subModuleId,
//     storageKey: "<module>_saved_views",
//     fullHeaders: MyReportHeaders,
//     screenAccess: check_access,
//     onViewChange: () => setListViewPage(0),
//   });
//
//   <CustomFilter filterHeaders={visibleHeaders} titleOverride={titleOverride} newButtonComponent={newButtonComponent} ... />
//   <MyReportTable headers={visibleHeaders} ... />
export const useSaveView = ({
  domain,
  userId,
  subModuleId,
  storageKey,
  scope = "default",
  fullHeaders,
  screenAccess,
  enabled = true,
  onRestoreScope,
  onViewChange,
  // Overrides SavedViewChips' default brand-green "active" chip color —
  // pass a screen's own accent color when it has an established color
  // language of its own to match (see SavedViewChips for why the default
  // is otherwise a fixed color rather than theme-relative).
  chipColor,
}) => {
  const saveViewButtonRef = useRef(null);

  const {
    savedViewsForScope,
    activeView,
    getVisibleHeaders,
    selectView,
    showAllHeaders,
    deleteView,
    createView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId,
    subModuleId,
    storageKey,
    scope,
    onRestoreScope,
    onViewChange,
  });

  const visibleHeaders = useMemo(() => {
    if (!enabled) return fullHeaders;
    return getVisibleHeaders(fullHeaders);
  }, [enabled, fullHeaders, getVisibleHeaders]);

  // SaveViewButton's picker only understands flat {key, name} rows — a
  // grouped table's fullHeaders (two-row header with children) is flattened
  // to its leaf columns (tagged with their parent group's name) here so the
  // picker can list/reorder/rename them; getVisibleHeaders regroups the
  // result back for the actual table.
  const flatFullHeaders = useMemo(
    () => (isGroupedHeaderList(fullHeaders) ? flattenHeaders(fullHeaders) : fullHeaders),
    [fullHeaders],
  );

  const titleOverride = useMemo(() => {
    if (!enabled) return null;

    return (
      <SavedViewChips
        views={savedViewsForScope}
        activeViewId={activeView?.id || null}
        onSelectView={selectView}
        onShowAllHeaders={showAllHeaders}
        onDeleteView={deleteView}
        onAddNew={(event) =>
          saveViewButtonRef.current?.openCreate(event.currentTarget)
        }
        disabled={fullHeaders.length === 0}
        {...(chipColor ? { activeColor: chipColor } : {})}
      />
    );
  }, [
    activeView,
    chipColor,
    deleteView,
    enabled,
    fullHeaders,
    savedViewsForScope,
    selectView,
    showAllHeaders,
  ]);

  const newButtonComponent = enabled ? (
    <SaveViewButton
      ref={saveViewButtonRef}
      headers={flatFullHeaders}
      screenAccess={screenAccess}
      activeView={activeView}
      onCreateSavedView={createView}
      onUpdateSavedView={updateView}
    />
  ) : null;

  return {
    visibleHeaders,
    titleOverride,
    newButtonComponent,
    activeView,
    savedViewsForScope,
  };
};
